import React, { useState, useRef, useEffect } from 'react';
import Login from './Login'

import microphone from './microphone.png'
import replay from './replay.png'
import hero from './hero.jpg'
import waiting from './waiting.gif'

import { NewRecorder } from './recorder'
import { log, fail, sleep, enterDown } from './util'
import * as api from './api'

const recorder = NewRecorder()

const sim = false
const BOT = 'elisa'

const conversation = new api.Conversation()

const SESSION_TOKEN = 'gptchat_session'

function App() {
  let sessionToken: string|null = null

  const [connected, setConnected] = useState(true)
  const [msg, setMsg] = useState('')
  const [messages,setMessages] = useState<api.Message[]>([{
    who: BOT,
    text: `Hi!  My name is Elisa.  I'm powered by GPTChat.`,
    ts: Date.now(),
  }])
  const [thinking,setThinking] = useState(false)
  const audio = useRef<HTMLAudioElement>(null)
  const scroll = useRef<HTMLDivElement>(null)
  let lastRecording: Blob|null = null

  // scroll to bottom of chat
  useEffect(() => {
    scroll.current?.scrollIntoView()
  }, [messages])

  // play media from the URL
  function play(url: string) {
    const el = audio.current
    if (el) {
      el.src = url
      el.play()
    }
  }

  async function login(token: string|null) {
    if (sim) {
      return
    }

    if (token != null) {
      try {
        await api.session(token)
        sessionToken = token
        setConnected(true)
        localStorage.setItem(SESSION_TOKEN, token)
      } catch(err) {
        sessionToken = null
      }
    }

    if (!sessionToken) {
      setConnected(false)
      localStorage.removeItem(SESSION_TOKEN)
    }
  }

  useEffect(() => {
    login(localStorage.getItem(SESSION_TOKEN))
  }, [])

  // play text as audio
  async function speak(msg: string) {
    if (sim) {
      console.log("saying", msg)
      await sleep(1000)
      return
    }
    play(await api.speak(msg))
  }

  // add chat message to window
  async function message(m: api.Message) {
    log(m)
    setMessages((current) => [...current, m])
  }

  // run/simulate conversation step
  async function _converse(prompt: string) {
    if (sim) {
      await sleep(1000)
      return 'now is the time for all good men to come to the aide of their country.'
    }

    return await conversation.converse(prompt)
  }

  // run/simulate transcription step
  async function _transcribe(blob: Blob) {
    if (sim) {
      await sleep(1000)
      return 'what is your quest'
    }

    return await api.transcribe(blob)
  }

  // full conversation interaction
  async function converse(prompt: string) {
    setMsg(prompt)
    message({
      who: 'you',
      text: prompt,
      ts: Date.now(),
    })
    setThinking(true)
    try {
      let resp = await _converse(prompt)
      setThinking(false)

      message({
        who: BOT,
        text: resp,
        ts: Date.now(),
      })
      await speak(resp)
    } catch(err) {
      setThinking(false)
      const msg = (err as any)?.message
      message({
        who: 'error',
        text: `⚠️ Error${msg ? ': '+msg : ''}`,
        ts: Date.now(),
      })
    }
  }

  // recording handler for handling recording completion
  async function talk(blob: Blob) {
    lastRecording = blob

    return await converse(await _transcribe(blob))
  }

  return (
    <>
      <Login open={!connected} onSubmit={login}/>
      <div className="App p-8 container mx-auto h-screen flex flex-row" role="main">
        <audio ref={audio}></audio>
        <div className="rounded-lg p-4 border flex-grow flex flex-col bg-slate-50">
          <div className="w-full h-full overflow-scroll space-y-2">
            {messages.map(m => 
              <div className={m.who === 'you' ? 'flex' : 'flex justify-end'} key={m.ts}>
                <div className={m.who === 'you' ? 'mr-2' : 'ml-2'}>
                  <div className={'rounded-2xl border px-4 py-2 mx-auto ' + (m.who === 'you' ? 'bg-blue-50' : m.who === 'error' ? 'bg-red-50' : 'bg-green-50')}>
                    <span>{m.text}</span>   
                  </div>
                </div>
              </div>
            )}
            { thinking &&  
              <div className="flex justify-end">
                <div className="ml-2">
                  <div className="rounded-2xl border px-4 py-2 mx-auto bg-green-50">
                    <span><img src={waiting} className="w-8 h-8"/></span>   
                  </div>
                </div>
              </div>
            }
            <div ref={scroll}></div>
          </div>
          <div className="h-auto mt-4">
            <div className="border w-auto flex rounded-2 p-2 bg-white">
              <textarea 
                  className="flex-grow p-2" 
                  rows={4} 
                  value={msg} 
                  onChange={e => setMsg(e.target.value)}
                  onKeyDown={enterDown(() => converse(msg))}>
              </textarea>
            </div>
          </div>
        </div>
        <div className="px-12 py-20 text-center flex flex-col">
          <div className="w-48">
            <img className="h-48 w-48 object-cover rounded-full" src={hero}/>
            <div className="text-2xl">Elisa</div>
          </div>
          <div className="flex-grow flex flex-col justify-center">
            <div className="w-48 text-sm object-center">This is a reference to&nbsp;
              <a href="https://en.wikipedia.org/wiki/ELIZA" className="text-blue-700 hover:text-blue-400" target="_blank">Eliza</a>.
              To chat, simple hold down the microphone button as you talk, like a walkie talkie.  Once you let go,
              Elisa (GPTChat) will think and reply.
            </div>

          </div>
          <div className="space-y-4">
            <button 
                className="border-2 rounded-full p-4 hover:bg-red-50 active:bg-red-500" 
                onMouseDown={async () => (await recorder).start(talk)} 
                onMouseUp={async () => (await recorder).stop()}>
              <img src={microphone}/>
            </button>

            {/* Nice debugging tool */}
            {/*<button 
                className="border-2 rounded-full p-1 hover:bg-green-50 active:bg-green-400"
                onClick={() => lastRecording && play(URL.createObjectURL(lastRecording))}>
              <img className="w-4 h-4" src={replay}/>
            </button>*/}

          </div>
        </div>
      </div>
    </>
  );
}

export default App
